import { Eye, EyeOff, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { Input } from '@/components/ui/input'
import API from '@/lib/api'

/**
 * Tajný klíč pro ověření podpisu webhooků (hlavička X-Invo-Signature).
 * Ve výchozím stavu je skrytý, jde zkopírovat a vygenerovat nový.
 */
export function WebhookSecret({ secret, onRotated }: { secret: string; onRotated: () => void }) {
  const [visible, setVisible] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [rotating, setRotating] = useState(false)

  const rotate = async () => {
    setRotating(true)
    try {
      await API.post('/api/v2/settings/webhook-secret', {})
      toast.success('Nový klíč je vygenerovaný. Nezapomeňte ho změnit i na svém serveru.')
      setConfirmOpen(false)
      onRotated()
    } catch {
      toast.error('Nový klíč se nepodařilo vygenerovat.')
    } finally {
      setRotating(false)
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Klíč pro ověření podpisu</p>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          type={visible ? 'text' : 'password'}
          value={secret}
          aria-label="Klíč pro ověření podpisu webhooků"
          className="font-mono text-[13px]"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? 'Skrýt klíč' : 'Zobrazit klíč'}
        >
          {visible ? <EyeOff /> : <Eye />}
        </Button>
        <CopyButton content={secret} variant="outline" size="icon" />
        <ConfirmButton
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Vygenerovat nový klíč?"
          description="Starý klíč přestane okamžitě platit. Dokud nový nenastavíte na svém serveru, nebude ověření podpisu procházet."
          confirmLabel={rotating ? 'Generuji...' : 'Vygenerovat nový'}
          onConfirm={rotate}
        >
          <Button type="button" variant="outline" onClick={() => setConfirmOpen(true)}>
            <RefreshCw />
            Nový klíč
          </Button>
        </ConfirmButton>
      </div>
      <p className="text-sm text-muted-foreground">
        Každý webhook má hlavičku <code className="font-mono text-[13px]">X-Invo-Signature</code> s podpisem HMAC-SHA256.
        Na svém serveru ho ověřte tímto klíčem, viz{' '}
        <Link to="https://invo.space/docs/api/webhooky" target="_blank" className="underline underline-offset-2">
          návod v dokumentaci
        </Link>
        .
      </p>
    </div>
  )
}
